import * as Stomp from 'stompjs';
import SockJS from 'sockjs-client';
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {

  private stompClient: any;
  private connected = false;
  private connecting = false;

  private subscriptions: { topic: string; callback: (msg: any) => void }[] = [];

  // 🔔 Subjects (same as old working code)
  private updateNotificationCountViaWebSocket = new Subject<any>();
  updateNotificationCountViaWebSocket$ =
    this.updateNotificationCountViaWebSocket.asObservable();

  private claimReceived = new Subject<any>();
  claimReceived$ = this.claimReceived.asObservable();

  private notificationReceived = new Subject<any>();
  notificationReceived$ = this.notificationReceived.asObservable();

  constructor(private authService: AuthService) {}

  /* -------------------- PRIVATE HELPERS -------------------- */

  private getHeaders() {
    return {
      Authorization: `Bearer ${this.authService.getToken()}`
    };
  }

  private sendProfileRegistration() {
    const user = this.authService.getUser();

    if (user?.defaultProfileId && this.connected) {
      console.log('Sending profileId to websocket:', user.defaultProfileId);

      this.stompClient.send(
        '/app/notification',
        {},
        JSON.stringify({ profileId: user.defaultProfileId })
      );
    }
  }

  private createConnection() {
    if (this.connected || this.connecting) return;

    this.connecting = true;

    const socket = new SockJS(`${environment.webSocketEndPoint}ws`);
    this.stompClient = Stomp.over(socket);

    // Debug + heartbeat (safe)
    this.stompClient.debug = console.log;
    this.stompClient.heartbeat.outgoing = 10000;
    this.stompClient.heartbeat.incoming = 10000;

    this.stompClient.connect(
      this.getHeaders(),
      () => {
        console.log('WebSocket Connected');
        this.connected = true;
        this.connecting = false;

        // Built-in subscriptions (from old code)
        this.subscribeInternalTopics();
        // Re-subscribe topics
        this.subscriptions.forEach(sub => {
          this.stompClient.subscribe(sub.topic, (msg: { body: string; }) => {
            sub.callback(JSON.parse(msg.body));
          });
        });

        // 🔥 REQUIRED (same as old code)
        this.sendProfileRegistration();
      },
      (error: any) => {
        console.error('WebSocket Error:', error);
        this.connected = false;
        this.connecting = false;

        if (this.authService.getToken()) {
          setTimeout(() => this.createConnection(), 5000);
        }
      }
    );
  }

  private subscribeInternalTopics() {

    // /user queue
    this.stompClient.subscribe('/user/queue/messages', (msg: { body: any; }) => {
      this.updateNotificationCountViaWebSocket.next(msg.body);
    });

    // Claim topic
    this.stompClient.subscribe('/topic/claim', (msg: { body: string; }) => {
      this.claimReceived.next(JSON.parse(msg.body));
    });

    // Notification count
    this.stompClient.subscribe('/user/queue/notificationCount', (msg: { body: string; }) => {
      this.notificationReceived.next(JSON.parse(msg.body));
    });
  }

  /* -------------------- PUBLIC API -------------------- */

  connect(topic: string, callback: (message: any) => void) {

    // Prevent duplicate topics
    if (!this.subscriptions.find(s => s.topic === topic)) {
      this.subscriptions.push({ topic, callback });
    }

    if (!this.connected) {
      this.createConnection();
    } else {
      this.stompClient.subscribe(topic, (msg: { body: string; }) => {
        callback(JSON.parse(msg.body));
      });

      // 🔥 IMPORTANT for already-connected case
      this.sendProfileRegistration();
    }
  }

  send(destination: string, body: any) {
    if (!this.connected) {
      console.warn('WebSocket not connected. Message not sent.');
      return;
    }
    this.stompClient.send(destination, {}, JSON.stringify(body));
  }

  disconnect() {
    if (this.stompClient && this.connected) {
      this.stompClient.disconnect(() => {
        console.log('WebSocket Disconnected');
        this.connected = false;
      });
    }
  }
}